import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { homeSideActions } from '@/mocks/home'
import HomeIcon from './HomeIcon'
import BannerCarousel from '@/components/common/BannerCarousel'
import { resetCardPointer, setCardPointer } from '../tilt'
import { useTypewriter } from '../typewriter'

// 标题严格控制在 7 个字符以内，配合单行占位避免换行和打字过程中的布局抖动
// highlight = 绿色高亮的字符区间 [start, end)，第一条保持原有设计（前缀绿）
const HERO_TITLES: { full: string; highlight: [number, number] }[] = [
  { full: '共建AI短剧', highlight: [0, 2] },
  { full: 'AI共创未来', highlight: [2, 4] },
  { full: '人人都能拍短剧', highlight: [5, 7] },
  { full: '灵感变现岛', highlight: [2, 5] },
  { full: 'AI短剧大本营', highlight: [4, 7] },
  { full: '一站式短剧生态', highlight: [3, 7] },
  { full: '故事交给AI拍', highlight: [4, 7] },
  { full: '短剧出海第一站', highlight: [4, 7] },
]
const TITLE_TEXTS = HERO_TITLES.map((t) => t.full)
// 最长文案做隐形占位，打字/删除过程中宽度不变
const TITLE_GHOST = TITLE_TEXTS.reduce((a, b) => (b.length > a.length ? b : a))

const HERO_SUBTITLES: { full: string; highlight?: [number, number] }[] = [
  { full: '从创意到爆款 · 让每个故事被世界看见', highlight: [4, 6] },
  { full: '从创意到爆款，一步到位' },
  { full: '写剧本 · 找搭档 · 发作品 · 赚收益' },
  { full: '10万+ 短剧人都在这里搞事情' },
  { full: '搭班子、出爆款、赚美金' },
  { full: '短剧出海，从这里开始' },
  { full: 'AI时代，做短剧的人都在这里' },
]
const SUBTITLE_TEXTS = HERO_SUBTITLES.map((s) => s.full)
const SUBTITLE_GHOST = SUBTITLE_TEXTS.reduce((a, b) => (b.length > a.length ? b : a))

/** 按高亮区间渲染已打出的文本（区间随打字进度截断） */
function HighlightRange({ text, range }: { text: string; range?: [number, number] }) {
  if (!range || text.length <= range[0]) return <>{text}</>
  const [start, end] = range
  return (
    <>
      {text.slice(0, start)}
      <span>{text.slice(start, Math.min(end, text.length))}</span>
      {text.length > end ? text.slice(end) : null}
    </>
  )
}

export interface HeroSectionProps {
  /** 首页开场加载动画是否正在播放 */
  isIntroActive?: boolean
}

export default function HeroSection({ isIntroActive = false }: HeroSectionProps) {
  const [showContent, setShowContent] = useState(false)

  useEffect(() => {
    if (isIntroActive) {
      setShowContent(false)
      return
    }

    // 等待开场加载动画完全结束退出后，经过 300ms 蓄力缓冲，优雅渐显主体内容
    const timer = window.setTimeout(() => {
      setShowContent(true)
    }, 300)
    return () => window.clearTimeout(timer)
  }, [isIntroActive])

  const title = useTypewriter({ texts: TITLE_TEXTS, typeInterval: 110, pauseTicks: 22, eraseStep: 2, enabled: showContent })
  const subtitle = useTypewriter({ texts: SUBTITLE_TEXTS, typeInterval: 80, pauseTicks: 26, eraseStep: 3, enabled: showContent })

  return (
    <section className="home-hero home-screen">
      {/* 2K/大屏高质感氛围光与微科技网格纹理 */}
      <div className="hero-glow hero-glow--left" aria-hidden="true"></div>
      <div className="hero-glow hero-glow--right" aria-hidden="true"></div>
      <div className="hero-glow hero-glow--center" aria-hidden="true"></div>
      <div className="hero-grid-pattern" aria-hidden="true"></div>

      <div className={`home-hero__content ${showContent ? 'home-hero__content--visible' : ''}`}>
        <aside className="home-hero__side-actions" aria-label="快捷入口">
          <div className="side-actions-header">
            <span className="side-actions-header__dot"></span>
            <span>创作生态通道</span>
          </div>
          {homeSideActions.map((action) => {
            const isExternal = action.to.startsWith('http://') || action.to.startsWith('https://');
            const cardContent = (
              <>
                <span className="side-action__icon"><HomeIcon name={action.icon} size={24} /></span>
                <span className="min-w-0 flex-1">
                  <strong>{action.title}</strong>
                  <small>{action.subtitle}</small>
                </span>
                <HomeIcon className="side-action__arrow" name="chevron-right" size={18} />
              </>
            );

            if (isExternal) {
              return (
                <a
                  key={action.title}
                  href={action.to}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`side-action side-action--${action.tone}`}
                  onPointerMove={setCardPointer}
                  onPointerLeave={resetCardPointer}
                >
                  {cardContent}
                </a>
              );
            }

            return (
              <Link
                key={action.title}
                to={action.to}
                className={`side-action side-action--${action.tone}`}
                onPointerMove={setCardPointer}
                onPointerLeave={resetCardPointer}
              >
                {cardContent}
              </Link>
            );
          })}
        </aside>

        <div className="home-hero__center">
          <div className="hero-orbit hero-orbit--outer" aria-hidden="true"></div>
          <div className="hero-orbit hero-orbit--inner" aria-hidden="true"></div>
          <div className="hero-orbit hero-orbit--ultra" aria-hidden="true"></div>
          <div className="hero-spark hero-spark--left">✦</div>
          <div className="hero-spark hero-spark--right">✦</div>
          <div className="hero-spark hero-spark--top-left">✦</div>
          <div className="hero-spark hero-spark--bottom-right">✦</div>
          <h1>灵搭岛</h1>
          <h2>
            <span className="hero-type">
              <span className="hero-type__ghost" aria-hidden="true">{TITLE_GHOST}</span>
              <span className="hero-type__text">
                <HighlightRange text={title.text} range={HERO_TITLES[title.index].highlight} />
                <i className="hero-caret" aria-hidden="true"></i>
              </span>
            </span>
          </h2>
          <p>
            <span className="hero-type">
              <span className="hero-type__ghost" aria-hidden="true">{SUBTITLE_GHOST}</span>
              <span className="hero-type__text">
                <HighlightRange text={subtitle.text} range={HERO_SUBTITLES[subtitle.index].highlight} />
                <i className="hero-caret" aria-hidden="true"></i>
              </span>
            </span>
          </p>
        </div>

        <aside className="home-hero__side-right" aria-label="爆款标杆">
          <BannerCarousel
            type="index"
            variant="stack"
            className="side-story w-full"
            height="160px"
            enablePreview
          />
        </aside>
      </div>
    </section>
  )
}

